export type ScreenShareResolutionPreset = '720p' | '1080p' | '1440p' | '4k';
export type ScreenShareFpsPreset = 30 | 60 | 120;

export interface ScreenShareSettings {
  resolution: ScreenShareResolutionPreset;
  fps: ScreenShareFpsPreset;
}

export const SCREEN_SHARE_SETTINGS_KEY = 'erozion_screen_share_settings';

export const DEFAULT_SCREEN_SHARE_SETTINGS: ScreenShareSettings = {
  resolution: '1080p',
  fps: 60,
};

export const SCREEN_SHARE_RESOLUTION_PRESETS: Record<ScreenShareResolutionPreset, {
  label: string;
  width: number;
  height: number;
}> = {
  '720p': { label: '720p', width: 1280, height: 720 },
  '1080p': { label: '1080p', width: 1920, height: 1080 },
  '1440p': { label: '1440p', width: 2560, height: 1440 },
  '4k': { label: '4K', width: 3840, height: 2160 },
};

export const SCREEN_SHARE_FPS_PRESETS: ScreenShareFpsPreset[] = [30, 60, 120];

const BITRATE_BY_PRESET: Record<ScreenShareResolutionPreset, Record<ScreenShareFpsPreset, number>> = {
  '720p': { 30: 3_000_000, 60: 5_000_000, 120: 8_000_000 },
  '1080p': { 30: 6_000_000, 60: 10_000_000, 120: 16_000_000 },
  '1440p': { 30: 12_000_000, 60: 20_000_000, 120: 30_000_000 },
  '4k': { 30: 25_000_000, 60: 45_000_000, 120: 80_000_000 },
};

function isResolutionPreset(value: unknown): value is ScreenShareResolutionPreset {
  return value === '720p' || value === '1080p' || value === '1440p' || value === '4k';
}

function isFpsPreset(value: unknown): value is ScreenShareFpsPreset {
  return value === 30 || value === 60 || value === 120;
}

export function getScreenShareSettings(): ScreenShareSettings {
  try {
    const raw = localStorage.getItem(SCREEN_SHARE_SETTINGS_KEY);
    if (!raw) return DEFAULT_SCREEN_SHARE_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<ScreenShareSettings>;
    return {
      resolution: isResolutionPreset(parsed.resolution) ? parsed.resolution : DEFAULT_SCREEN_SHARE_SETTINGS.resolution,
      fps: isFpsPreset(parsed.fps) ? parsed.fps : DEFAULT_SCREEN_SHARE_SETTINGS.fps,
    };
  } catch {
    return DEFAULT_SCREEN_SHARE_SETTINGS;
  }
}

export function setScreenShareSettings(settings: ScreenShareSettings): ScreenShareSettings {
  const next = {
    resolution: isResolutionPreset(settings.resolution) ? settings.resolution : DEFAULT_SCREEN_SHARE_SETTINGS.resolution,
    fps: isFpsPreset(settings.fps) ? settings.fps : DEFAULT_SCREEN_SHARE_SETTINGS.fps,
  };
  localStorage.setItem(SCREEN_SHARE_SETTINGS_KEY, JSON.stringify(next));
  return next;
}

export function getScreenShareEncoding(settings = getScreenShareSettings()) {
  return {
    maxBitrate: BITRATE_BY_PRESET[settings.resolution][settings.fps],
    maxFramerate: settings.fps,
    priority: 'high' as RTCPriorityType,
  };
}

export function getScreenShareResolution(settings = getScreenShareSettings()) {
  return SCREEN_SHARE_RESOLUTION_PRESETS[settings.resolution];
}
